import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { OverflowMenu, OverflowMenuItem } from '@carbon/react';
import {
  ArrowRight,
  Calendar,
  ChevronDown,
  Time,
  UserMultiple,
} from '@carbon/icons-react';
import Collapsible from '../Collapsible';
import AreaNotesPanel from './AreaNotesPanel';
import { getRelativeDayLabel } from '../workday/workDayUtils';
import './OnShiftPanel.css';

// Tabovi profila zaposlenog (deep-link `?tab=` na profilu zaposlenog)
const EMPLOYEE_TABS = [
  { id: 'general', label: 'Opšte informacije' },
  { id: 'permissions', label: 'Dozvole' },
  { id: 'shifts', label: 'Smene' },
  { id: 'tasks', label: 'Zadaci' },
  { id: 'activity', label: 'Aktivnost' },
];

// Backend šalje vreme u UTC - prikazujemo ga u CET/CEST (Evropa/Belgrade, Srbija)
const TIME_ZONE = 'Europe/Belgrade';

// Paleta boja za avatare zaposlenih
const AVATAR_COLORS = ['#9fb746', '#0f62fe', '#8a3ffc', '#f1c21b', '#24a148', '#0072c3', '#d02670'];

// Srpska množina: 1 zaposleni, 2-4 zaposlena, 5+ zaposlenih
const plural = (count, one, few, many) => {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
};

const formatTime = (dateString) => {
  if (!dateString) return '--:--';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '--:--';
  return new Intl.DateTimeFormat('sr-RS', {
    timeZone: TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
};

const getShiftStart = (emp) =>
  emp?.shiftStartTime || emp?.startTime || emp?.shiftStart || emp?.start || null;
const getShiftEnd = (emp) =>
  emp?.shiftEndTime || emp?.endTime || emp?.shiftEnd || emp?.end || null;

const formatShiftRange = (start, end) => {
  const hasStart = Boolean(start);
  const hasEnd = Boolean(end);
  if (hasStart && hasEnd) return `${formatTime(start)} – ${formatTime(end)}`;
  if (hasStart) return formatTime(start);
  if (hasEnd) return formatTime(end);
  return '--:--';
};

const initialsOf = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts.slice(0, 2).map((p) => p[0]).join('').toUpperCase();
};

const colorOf = (name) => {
  const str = String(name || '');
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

// Avatar zaposlenog: prikazuje fotografiju (imgSrc) ako postoji, inače inicijale.
// Ako se slika ne učita, vraća se na inicijale.
const EmployeeAvatar = ({ name, imgSrc }) => {
  const [failed, setFailed] = useState(false);
  const showImage = Boolean(imgSrc) && !failed;

  if (showImage) {
    return (
      <img
        className="adash-shift__avatar adash-shift__avatar--img"
        src={imgSrc}
        alt={name || ''}
        loading="lazy"
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <span
      className="adash-shift__avatar"
      style={{ backgroundColor: colorOf(name) }}
    >
      {initialsOf(name)}
    </span>
  );
};

const OnShiftPanel = ({
  data,
  loading,
  defaultCollapsed = false,
  openToken = 0,
  title = 'Trenutno u smeni',
  emptyText = 'Trenutno nema zaposlenih u smeni',
  sectionId = 'adash-on-shift',
  // Mapa "naziv oblasti -> areaId". Kada je prosleđena, ispod svake oblasti se
  // prikazuju beleške smene za tu oblast (GET /api/ShiftNote/area-today/{areaId}).
  areaIdsByArea = null,
}) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(!defaultCollapsed);

  // Kada se openToken promeni (npr. klik na KPI karticu "U smeni"), otvori panel
  const prevOpenTokenRef = useRef(openToken);
  useEffect(() => {
    if (prevOpenTokenRef.current !== openToken) {
      prevOpenTokenRef.current = openToken;
      setIsExpanded(true);
    }
  }, [openToken]);

  const areas = Array.isArray(data?.areas) ? data.areas : [];
  // `current-shifts` vraća `totalEmployeesOnShift`, a `next-shift` vraća `totalEmployees`
  const totalEmployees = data?.totalEmployeesOnShift ?? data?.totalEmployees ?? 0;
  const totalAreas = data?.totalAreas ?? areas.length;

  // Ako smena nije danas (npr. "next-shift" vraća sutrašnji dan kao fallback),
  // naglasi to u zaglavlju (npr. badge "Sutra").
  const shiftStartForDay =
    data?.nextShiftStartTime ||
    areas[0]?.employees?.[0]?.shiftStartTime ||
    getShiftStart(areas[0]) ||
    null;
  const relativeDayLabel = shiftStartForDay ? getRelativeDayLabel(shiftStartForDay) : '';
  const showDayBadge =
    Boolean(relativeDayLabel) &&
    relativeDayLabel !== 'Danas' &&
    relativeDayLabel !== 'Sledeća smena';

  return (
    <section className="adash-panel adash-shift" id={sectionId}>
      <button
        type="button"
        className={`adash-panel__toggle ${isExpanded ? 'is-expanded' : ''}`}
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}
      >
        <span className="adash-panel__toggle-left">
          <span className="adash-panel__icon"><UserMultiple size={20} /></span>
          <span className="adash-panel__head-text">
            <span className="adash-panel__title">
              {title}
              {showDayBadge && (
                <span className="adash-panel__day-badge">{relativeDayLabel}</span>
              )}
            </span>
            <span className="adash-panel__sub">
              {totalEmployees} {plural(totalEmployees, 'zaposleni', 'zaposlena', 'zaposlenih')}
              {' · '}
              {totalAreas} {plural(totalAreas, 'oblast', 'oblasti', 'oblasti')}
            </span>
          </span>
        </span>
        <ChevronDown
          size={20}
          className={`adash-panel__chevron ${isExpanded ? 'is-expanded' : ''}`}
        />
      </button>

      <Collapsible open={isExpanded}>
        <div className="adash-shift__body">
          {loading ? (
            <div className="adash-shift__skeleton">
              {[0, 1, 2, 3].map((i) => (
                <span className="adash-shift__skeleton-row" key={i} />
              ))}
            </div>
          ) : totalEmployees === 0 ? (
            <div className="adash-shift__empty">{emptyText}</div>
          ) : (
            areas.map((area, areaIndex) => {
              const employees = Array.isArray(area.employees) ? area.employees : [];
              const count = area.employeeCount ?? employees.length;
              const areaId = areaIdsByArea?.[area.area] || null;
              return (
                <div className="adash-shift__group" key={`${area.area}-${areaIndex}`}>
                  <div className="adash-shift__group-head">
                    <span className="adash-shift__group-name">{area.area}</span>
                    <span className="adash-shift__group-count">{count}</span>
                  </div>

                  {employees.map((emp) => (
                    <div className="adash-shift__row-wrap" key={emp.employeeId}>
                      <button
                        type="button"
                        className="adash-shift__row"
                        onClick={() =>
                          navigate(`/employees/${emp.employeeId}`, {
                            state: { from: { label: 'Dashboard', route: '/admin-dashboard' } },
                          })
                        }
                        title={`Otvori profil: ${emp.employeeName}`}
                      >
                        <EmployeeAvatar name={emp.employeeName} imgSrc={emp.imgSrc} />
                        <span className="adash-shift__text">
                          <span className="adash-shift__name">{emp.employeeName}</span>
                          <span className="adash-shift__role">{emp.employeeTypeName}</span>
                        </span>
                        <span className="adash-shift__time">
                          <Time size={14} />
                          {formatShiftRange(
                            getShiftStart(emp) || getShiftStart(area),
                            getShiftEnd(emp) || getShiftEnd(area)
                          )}
                        </span>
                      </button>

                      {/* 3 tačkice: linkovi ka tabovima profila zaposlenog (bez tooltip-a) */}
                      <OverflowMenu
                        size="sm"
                        flipped
                        className="adash-shift__menu"
                        iconDescription={`Opcije: ${emp.employeeName}`}
                      >
                        {EMPLOYEE_TABS.map((tab) => (
                          <OverflowMenuItem
                            key={tab.id}
                            itemText={tab.label}
                            onClick={() =>
                              navigate(`/employees/${emp.employeeId}?tab=${tab.id}`, {
                                state: {
                                  from: { label: 'Dashboard', route: '/admin-dashboard' },
                                },
                              })
                            }
                          />
                        ))}
                      </OverflowMenu>
                    </div>
                  ))}

                  {/* Beleške smene za ovu oblast (expand/collapse) */}
                  {areaId && (
                    <AreaNotesPanel
                      areaId={areaId}
                      onOpenResident={(residentId) =>
                        navigate(`/residents/${residentId}`, {
                          state: { from: { label: 'Dashboard', route: '/admin-dashboard' } },
                        })
                      }
                    />
                  )}
                </div>
              );
            })
          )}

          <button
            type="button"
            className="adash-panel__link"
            onClick={() => navigate('/shift-management')}
          >
            <span className="adash-panel__link-label">
              <Calendar size={16} />
              Kalendar smena
            </span>
            <ArrowRight size={16} className="adash-panel__link-arrow" />
          </button>
        </div>
      </Collapsible>
    </section>
  );
};

export default OnShiftPanel;
